import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { CreditCard, Phone, CheckCircle, XCircle, Clock, DollarSign, Upload, Info, TrendingUp, ArrowRight, ArrowLeft } from 'lucide-react';

const Payment = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [unitName, setUnitName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [totalFee, setTotalFee] = useState(0);
  const [paidSoFar, setPaidSoFar] = useState(0);
  const [balance, setBalance] = useState(0);

  // Cash payment fields
  const [cashPaymentNotes, setCashPaymentNotes] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);

  // Status tracking
  const [pollingInterval, setPollingInterval] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | initiating | pending | pending-approval | confirmed | failed | timeout
  const [lastPaymentAmount, setLastPaymentAmount] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchEnrollmentData = useCallback(async () => {
    try {
      const response = await axios.get(`/student-units/status/${unitId}/${user.id}`);
      if (response.data) {
        const total = parseFloat(response.data.totalAmount || 0);
        const paid = parseFloat(response.data.paidAmount || 0);
        const rem = total - paid;
        setTotalFee(total);
        setPaidSoFar(paid);
        setBalance(rem);
        if (rem > 0 && !amount) setAmount(rem.toString());
        if (response.data.unit?.name) setUnitName(response.data.unit.name);
      }
    } catch (err) {
      console.error('Error fetching enrollment:', err);
    }
  }, [unitId, user.id, amount]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`/students/${user.id}?includeCourse=true&includeCampus=true`);
        const data = res.data;
        setStudentData(data);
        
        // Auto-select enabled payment method if one is disabled
        if (data.institution) {
          if (!data.institution.allowMpesaPayment && data.institution.allowCashPayment) {
            setPaymentMethod('cash');
          } else if (data.institution.allowMpesaPayment && !data.institution.allowCashPayment) {
            setPaymentMethod('mpesa');
          }
        }

        const enrollment = await axios.get(`/student-units/status/${unitId}/${user.id}`);
        if (enrollment.data) {
          const total = parseFloat(enrollment.data.totalAmount || 0);
          const paid = parseFloat(enrollment.data.paidAmount || 0);
          const rem = total - paid;
          setTotalFee(total);
          setPaidSoFar(paid);
          setBalance(rem);
          setAmount(rem.toString());
          if (enrollment.data.unit?.name) setUnitName(enrollment.data.unit.name);

          if (total === 0 && res.data?.campusId) {
            const costRes = await axios.get(`/unit-costs/current/${unitId}/${res.data.campusId}`);
            if (costRes.data?.cost) {
              const cost = parseFloat(costRes.data.cost);
              setTotalFee(cost);
              setBalance(cost);
              setAmount(cost.toString());
            }
          }
        }
      } catch (err) {
        console.error('Init error:', err);
      }
    };
    init();
  }, [unitId, user.id]);

  useEffect(() => {
    return () => { if (pollingInterval) clearInterval(pollingInterval); };
  }, [pollingInterval]);

  const formatPhoneNumber = (phone) => {
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');
    if (cleaned.startsWith('0')) cleaned = '254' + cleaned.substring(1);
    if (!cleaned.startsWith('254') && !cleaned.startsWith('+')) cleaned = '254' + cleaned;
    return cleaned.replace(/^\+/, '');
  };

  const initiatePayment = async () => {
    const formattedPhone = formatPhoneNumber(phoneNumber);
    if (!/^254[0-9]{9}$/.test(formattedPhone)) {
      alert('Please enter a valid Kenyan phone number (e.g., 0712345678)');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) { alert('Please enter a valid amount'); return; }
    if (parseFloat(amount) > balance + 0.01) {
      alert(`Amount cannot exceed remaining balance of KES ${balance.toLocaleString()}`);
      return;
    }
    if (paidSoFar === 0 && parseFloat(amount) < totalFee / 2) {
      alert(`For the first installment, you must pay at least 50% of the total fee (KES ${(totalFee / 2).toLocaleString()}).`);
      return;
    }

    try {
      setLoading(true);
      setPaymentStatus('initiating');
      const response = await axios.post('/payments/initiate-mpesa', {
        studentId: user.id, unitId: parseInt(unitId),
        phoneNumber: formattedPhone, amount: parseFloat(amount)
      });

      if (response.data.success) {
        setPaymentStatus('pending');
        setLastPaymentAmount(parseFloat(amount));
        alert(response.data.customerMessage || 'Check your phone and enter M-Pesa PIN to complete payment.');
        startPaymentPolling(response.data.checkoutRequestId);
      } else {
        setPaymentStatus('failed');
        alert(response.data.message || 'Failed to initiate payment');
      }
    } catch (err) {
      setPaymentStatus('failed');
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startPaymentPolling = (requestId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`/payments/status/${requestId}`);
        if (response.data.status === 'confirmed') {
          clearInterval(interval);
          setPollingInterval(null);
          setPaymentStatus('confirmed');
          await fetchEnrollmentData();
          const confirmedAmount = response.data.amount || lastPaymentAmount;
          setSuccessMessage(`KES ${parseFloat(confirmedAmount).toLocaleString()} payment confirmed successfully!`);
        }
      } catch (err) { /* keep polling */ }
    }, 5000);
    setPollingInterval(interval);
    setTimeout(() => { clearInterval(interval); setPollingInterval(null); }, 300000);
  };

  const initiateCashPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) { alert('Please enter a valid amount'); return; }
    if (parseFloat(amount) > balance + 0.01) {
      alert(`Amount cannot exceed remaining balance of KES ${balance.toLocaleString()}`);
      return;
    }
    if (paidSoFar === 0 && parseFloat(amount) < totalFee / 2) {
      alert(`For the first installment, you must pay at least 50% of the total fee (KES ${(totalFee / 2).toLocaleString()}).`);
      return;
    }
    try {
      setLoading(true);
      setPaymentStatus('submitting');
      let receiptImageBase64 = null;
      if (receiptImage) {
        receiptImageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(receiptImage);
        });
      }
      const response = await axios.post('/payments/initiate-cash', {
        studentId: user.id, unitId: parseInt(unitId),
        amount: parseFloat(amount),
        paymentNotes: cashPaymentNotes.trim(),
        referenceNumber: referenceNumber.trim(),
        receiptImage: receiptImageBase64
      });
      if (response.data.success) {
        setPaymentStatus('pending-approval');
        setLastPaymentAmount(parseFloat(amount));
        setSuccessMessage(`Cash payment request of KES ${parseFloat(amount).toLocaleString()} submitted. Waiting for lecturer approval.`);
      } else {
        setPaymentStatus('failed');
        alert(response.data.message || 'Failed to submit cash payment');
      }
    } catch (err) {
      setPaymentStatus('failed');
      alert('Failed to submit cash payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForAnotherPayment = async () => {
    setPaymentStatus('idle');
    setAmount('');
    setReferenceNumber('');
    setCashPaymentNotes('');
    setReceiptImage(null);
    setReceiptPreview(null);
    setSuccessMessage('');
    await fetchEnrollmentData();
  };

  const progressPct = totalFee > 0 ? Math.min((paidSoFar / totalFee) * 100, 100) : 0;
  const isProcessing = ['initiating', 'pending', 'submitting'].includes(paymentStatus);

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="p-2.5 bg-white rounded-xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight">Unit Enrollment</h1>
          <p className="text-xs text-gray-500 font-medium">{unitName || 'Loading unit details...'}</p>
        </div>
      </div>

      {/* Installment Policy Info */}
      {paidSoFar === 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3.5 shadow-sm">
          <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
            <Info className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-blue-900 leading-tight">Installment Policy</h3>
            <p className="text-xs text-blue-700 mt-1 leading-relaxed">
              Paying at least <b>50% (KES {(totalFee / 2).toLocaleString()})</b> as your first installment will immediately grant you full access to learning materials, quizzes, and assessments.
            </p>
          </div>
        </div>
      )}

      {/* Balance Progress Card */}
      {totalFee > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Payment Summary</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center p-3 bg-gray-50 rounded-xl">
              <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">Total Fee</p>
              <p className="text-base font-black text-gray-900">KES {totalFee.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <p className="text-[10px] text-green-600 uppercase font-bold tracking-widest mb-1">Paid</p>
              <p className="text-base font-black text-green-700">KES {paidSoFar.toLocaleString()}</p>
            </div>
            <div className={`text-center p-3 rounded-xl ${balance <= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
              <p className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${balance <= 0 ? 'text-green-600' : 'text-orange-600'}`}>Balance</p>
              <p className={`text-base font-black ${balance <= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                {balance <= 0 ? 'CLEARED' : `KES ${balance.toLocaleString()}`}
              </p>
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-green-500 rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-gray-400">{progressPct.toFixed(0)}% paid</span>
            {balance > 0 && <span className="text-[10px] text-orange-500 font-bold">KES {balance.toLocaleString()} remaining</span>}
          </div>
        </div>
      )}

      {/* Success / Pending Approval State */}
      {(paymentStatus === 'confirmed' || paymentStatus === 'pending-approval') && (
        <div className={`rounded-xl p-5 border ${paymentStatus === 'confirmed' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-start gap-3">
            {paymentStatus === 'confirmed'
              ? <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              : <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
            }
            <div className="flex-1">
              <p className={`font-bold text-sm ${paymentStatus === 'confirmed' ? 'text-green-900' : 'text-blue-900'}`}>
                {paymentStatus === 'confirmed' ? 'Payment Confirmed!' : 'Awaiting Lecturer Approval'}
              </p>
              <p className={`text-xs mt-0.5 ${paymentStatus === 'confirmed' ? 'text-green-700' : 'text-blue-700'}`}>
                {successMessage}
              </p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            {paymentStatus === 'confirmed' && balance > 0 && (
              <button onClick={resetForAnotherPayment} className="flex-1 text-sm font-bold py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5">
                Pay Remaining Balance <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => navigate('/units')} className={`flex-1 text-sm font-bold py-2.5 rounded-xl border transition-colors ${paymentStatus === 'confirmed' && balance > 0 ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
              {paymentStatus === 'confirmed' && balance <= 0 ? 'Go to My Units' : 'Back to Units'}
            </button>
          </div>
        </div>
      )}

      {/* Payment Form — hidden after confirmed/pending-approval */}
      {!['confirmed', 'pending-approval'].includes(paymentStatus) && balance > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          {/* Method Selector */}
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Payment Method</h2>
          <div className="grid grid-cols-2 gap-2 mb-5">
            <button
              onClick={() => setPaymentMethod('mpesa')}
              disabled={isProcessing || studentData?.institution?.allowMpesaPayment === false}
              className={`p-3.5 border-2 rounded-xl text-center transition-all relative ${
                paymentMethod === 'mpesa' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              } ${studentData?.institution?.allowMpesaPayment === false ? 'opacity-40 grayscale cursor-not-allowed' : ''}`}
            >
              <CreditCard className={`w-5 h-5 mx-auto mb-1.5 ${paymentMethod === 'mpesa' ? 'text-blue-600' : 'text-gray-400'}`} />
              <span className={`text-xs font-bold ${paymentMethod === 'mpesa' ? 'text-blue-700' : 'text-gray-600'}`}>M-Pesa STK</span>
              {studentData?.institution?.allowMpesaPayment === false && (
                <span className="absolute top-1 right-1 bg-red-100 text-red-600 text-[8px] px-1 rounded font-bold">DISABLED</span>
              )}
            </button>
            <button
              onClick={() => setPaymentMethod('cash')}
              disabled={isProcessing || studentData?.institution?.allowCashPayment === false}
              className={`p-3.5 border-2 rounded-xl text-center transition-all relative ${
                paymentMethod === 'cash' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
              } ${studentData?.institution?.allowCashPayment === false ? 'opacity-40 grayscale cursor-not-allowed' : ''}`}
            >
              <DollarSign className={`w-5 h-5 mx-auto mb-1.5 ${paymentMethod === 'cash' ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={`text-xs font-bold ${paymentMethod === 'cash' ? 'text-green-700' : 'text-gray-600'}`}>Cash / Other</span>
              {studentData?.institution?.allowCashPayment === false && (
                <span className="absolute top-1 right-1 bg-red-100 text-red-600 text-[8px] px-1 rounded font-bold">DISABLED</span>
              )}
            </button>
          </div>

          {/* Amount Input */}
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Amount (KES)</label>
            <input
              type="number"
              className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none font-bold text-gray-900 text-lg"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              max={balance}
              placeholder={`Up to ${balance.toLocaleString()}`}
              disabled={isProcessing}
            />
            <div className="flex justify-between mt-1.5">
              <p className="text-[11px] text-gray-400">
                {paidSoFar === 0 
                  ? `Min First Installment (50%): KES ${(totalFee / 2).toLocaleString()}`
                  : 'Minimum: KES 1 | You can pay any amount'
                }
              </p>
              <button onClick={() => setAmount(balance.toString())} className="text-[11px] text-indigo-500 font-bold hover:underline">Pay full balance</button>
            </div>
          </div>

          {/* M-Pesa Fields */}
          {paymentMethod === 'mpesa' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">M-Pesa Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none"
                    placeholder="0712345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={isProcessing}
                  />
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                <p className="text-xs font-bold text-blue-800 mb-1">How M-Pesa payment works:</p>
                <ol className="text-xs text-blue-700 space-y-0.5">
                  <li>1. Enter phone number &amp; amount, then tap Pay</li>
                  <li>2. A prompt will appear on your phone</li>
                  <li>3. Enter your M-Pesa PIN to complete</li>
                  <li>4. Access is granted immediately after confirmation</li>
                </ol>
              </div>
            </div>
          )}

          {/* Cash Fields */}
          {paymentMethod === 'cash' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Reference / Receipt # (Optional)</label>
                <input type="text" className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-sm" placeholder="e.g., Receipt #12345" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Notes (Optional)</label>
                <textarea className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-400 outline-none text-sm resize-none" rows={2} placeholder="Additional payment details..." value={cashPaymentNotes} onChange={(e) => setCashPaymentNotes(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  <Upload className="inline w-3 h-3 mr-1" />
                  Receipt Photo (Optional)
                </label>
                <input type="file" accept="image/*,.pdf" onChange={(e) => { const f = e.target.files[0]; if (f) { setReceiptImage(f); const r = new FileReader(); r.onload = (ev) => setReceiptPreview(ev.target.result); r.readAsDataURL(f); } }} className="w-full text-xs border border-gray-300 rounded-xl p-2" />
                {receiptPreview && (
                  <div className="relative mt-2">
                    <img src={receiptPreview} alt="Receipt" className="w-full h-28 object-cover rounded-xl border" />
                    <button onClick={() => { setReceiptImage(null); setReceiptPreview(null); }} className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">×</button>
                  </div>
                )}
              </div>
              <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                <p className="text-xs font-bold text-green-800 mb-1">Cash payment process:</p>
                <ol className="text-xs text-green-700 space-y-0.5">
                  <li>1. Pay the amount in cash to your lecturer</li>
                  <li>2. Submit this request with reference details</li>
                  <li>3. Your lecturer approves it to activate access</li>
                  <li>4. You can make multiple partial payments</li>
                </ol>
              </div>
            </div>
          )}


          {/* Status Banner */}
          {paymentStatus === 'failed' && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
              <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700 font-medium">Payment failed. Please try again.</p>
            </div>
          )}
          {paymentStatus === 'pending' && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-orange-50 rounded-xl border border-orange-100">
              <Clock className="w-4 h-4 text-orange-500 animate-spin flex-shrink-0" />
              <p className="text-xs text-orange-700 font-medium">Waiting for payment confirmation on your phone...</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-5">
            <button
              onClick={paymentMethod === 'mpesa' ? initiatePayment : initiateCashPayment}
              disabled={loading || isProcessing}
              className={`flex-1 py-3 rounded-xl font-bold text-white transition-colors flex items-center justify-center gap-2 ${paymentMethod === 'mpesa' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading || isProcessing ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
              ) : paymentMethod === 'mpesa' ? 'Pay with M-Pesa' : 'Submit Cash Payment'}
            </button>
            <button onClick={() => navigate('/units')} disabled={isProcessing} className="px-4 py-3 rounded-xl border border-gray-300 text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Fully paid state */}
      {balance <= 0 && paymentStatus === 'idle' && totalFee > 0 && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-5 text-center">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <p className="font-bold text-green-900">Unit Fully Paid</p>
          <p className="text-xs text-green-700 mt-1">Your full payment of KES {totalFee.toLocaleString()} has been received.</p>
          <button onClick={() => navigate('/units')} className="mt-4 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors text-sm">
            Go to My Units
          </button>
        </div>
      )}
    </div>
  );
};

export default Payment;
